import { ChangeDetectionStrategy, Component, input, output, signal } from '@angular/core';
import { getTransferFiles, renamePastedImage } from '@shared/helpers/pasted-files.helper';

/**
 * Field to paste screenshots into (click, then Cmd+V / Ctrl+V) or to drop files on. Emits the files matching
 * `accept` (`pasted` or `dropped`); pasted images are named `<pastedName> <n>`. `pastedNothing` fires when the
 * clipboard holds no file.
 */
@Component({
  selector: 'paste-drop-zone',
  templateUrl: './paste-drop-zone.component.html',
  styleUrls: ['./paste-drop-zone.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PasteDropZoneComponent {
  accept = input<string>('image/*');
  label = input<string>('PasteDropZone.label');
  /** Shown while the zone has focus, so it is clear where a paste goes. */
  focusedLabel = input<string>('PasteDropZone.focused');
  pastedName = input<string>('Screenshot');
  disabled = input<boolean>(false);

  pasted = output<File[]>();
  dropped = output<File[]>();
  pastedNothing = output<void>();

  protected readonly dragging = signal(false);
  protected readonly focused = signal(false);
  private pastedCount = 0;
  private dragDepth = 0;

  /** Pasted files from outside the zone (e.g. a page-wide paste) go through the same naming. */
  nameClipboardFiles(files: File[]): File[] {
    return files.map((file) =>
      file.type.startsWith('image/')
        ? renamePastedImage(file, `${this.pastedName()} ${++this.pastedCount}`)
        : file
    );
  }

  protected onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    if (this.disabled()) return;

    const files = getTransferFiles(event.clipboardData, this.accept());
    if (!files.length) {
      this.pastedNothing.emit();
      return;
    }
    this.pasted.emit(this.nameClipboardFiles(files));
  }

  // Only pasting is allowed in the zone, typing is not.
  protected onKeydown(event: KeyboardEvent): void {
    const paste = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'v';
    if (!paste && event.key !== 'Tab') event.preventDefault();
  }

  protected onDragEnter(event: DragEvent): void {
    event.preventDefault();
    if (this.disabled()) return;
    this.dragDepth++;
    this.dragging.set(true);
  }

  protected onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  protected onDragLeave(): void {
    this.dragDepth = Math.max(this.dragDepth - 1, 0);
    if (!this.dragDepth) this.dragging.set(false);
  }

  protected onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragDepth = 0;
    this.dragging.set(false);
    if (this.disabled()) return;

    const files = getTransferFiles(event.dataTransfer, this.accept());
    if (files.length) this.dropped.emit(files);
  }
}
